import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { ZendeskSourceSettingsApi } from "./ZendeskSourceSettings.Api";
import type { ZendeskSourceSettingsViewModel } from "./ZendeskSourceSettings.ViewModel.types";
import {
  ZENDESK_DEFAULT_ASSIGNED_TO_ME,
  ZENDESK_DEFAULT_DONE_STATUS,
  ZENDESK_DEFAULT_MODE,
  ZENDESK_DEFAULT_STATUSES,
  ZENDESK_DOMAIN_SUFFIX,
  ZENDESK_DONE_STATUSES,
  ZENDESK_PRIORITIES,
  ZENDESK_STATUSES,
  ZENDESK_TICKET_TYPES,
  type ZendeskConfiguration,
  type ZendeskMode,
  type ZendeskViewReference,
} from "./ZendeskSourceSettings.types";

const NONE_OPTION = "__none";

/** Keeps only the values that belong to the option list, in the option list's order. */
const pickKnown = <T extends string>(options: readonly T[], values: string[]): T[] =>
  options.filter((option) => values.includes(option));

const toProfileIdentity = (model: SourceSettingsModel): string =>
  `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;

export const useZendeskSourceSettingsViewModel = (
  model: SourceSettingsModel,
): ZendeskSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(toProfileIdentity(model));

  const [views, setViews] = useState<ZendeskViewReference[]>([]);
  const [isLoadingViews, setIsLoadingViews] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Zendesk && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<ZendeskConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const mode: ZendeskMode = configuration.mode === "View" ? "View" : ZENDESK_DEFAULT_MODE;
  const isViewMode = mode === "View";
  const isFilterMode = !isViewMode;

  // Fill the defaults the editor shows on first render for this connection.
  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    const current = parseConnectionConfiguration<ZendeskConfiguration>(
      editingConnectionRef.current.configuration,
    );
    if (current.mode && current.statuses && current.doneStatus) {
      return;
    }

    // Fills only what is missing, so it is safe to apply to the saved settings too.
    updateConnectionConfiguration<ZendeskConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        mode: cfg.mode ?? ZENDESK_DEFAULT_MODE,
        statuses: cfg.statuses ?? [...ZENDESK_DEFAULT_STATUSES],
        doneStatus: cfg.doneStatus ?? ZENDESK_DEFAULT_DONE_STATUS,
        assignedToMe: cfg.assignedToMe ?? ZENDESK_DEFAULT_ASSIGNED_TO_ME,
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  // A different signed-in account sees different views, so the chosen view no longer applies.
  const profileIdentity = toProfileIdentity(model);
  useEffect(() => {
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = profileIdentity;

    if (!isVisible || previousProfileIdentity === profileIdentity) {
      return;
    }

    setViews([]);
    updateConnectionConfiguration<ZendeskConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({ ...cfg, view: null }),
    );
  }, [isVisible, profileIdentity]);

  useEffect(() => {
    const loadViews = async (): Promise<void> => {
      if (!isVisible || !isViewMode || !model.boardId || !editingConnectionId) {
        setViews([]);
        setIsLoadingViews(false);
        return;
      }

      const zendeskApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<ZendeskSourceSettingsApi>(
          SourceTypeIds.Zendesk,
        );
      if (!zendeskApi) {
        setViews([]);
        setIsLoadingViews(false);
        return;
      }

      setIsLoadingViews(true);
      setHasError(false);
      try {
        setViews(await zendeskApi.getViews(model.boardId, editingConnectionId));
      } catch {
        setViews([]);
        setHasError(true);
      } finally {
        setIsLoadingViews(false);
      }
    };

    void loadViews();
  }, [editingConnectionId, isViewMode, isVisible, model.boardId]);

  const selectedStatuses = pickKnown(ZENDESK_STATUSES, configuration.statuses ?? []);
  const selectedPriorities = pickKnown(ZENDESK_PRIORITIES, configuration.priorities ?? []);
  const selectedTicketTypes = pickKnown(ZENDESK_TICKET_TYPES, configuration.ticketTypes ?? []);
  const selectedDoneStatus =
    ZENDESK_STATUSES.find((status) => status === configuration.doneStatus) ?? null;
  const selectedView = configuration.view ?? null;
  const subdomain = configuration.subdomain?.trim() ?? "";
  const accountHost = subdomain ? `${subdomain}${ZENDESK_DOMAIN_SUFFIX}` : "";

  const update = (updater: (cfg: Partial<ZendeskConfiguration>) => Partial<ZendeskConfiguration>): void => {
    updateConnectionConfiguration<ZendeskConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      updater,
    );
  };

  return {
    isVisible,
    accountHost,
    hasAccountHost: accountHost.length > 0,
    mode,
    isFilterMode,
    isViewMode,
    statusOptions: ZENDESK_STATUSES,
    priorityOptions: ZENDESK_PRIORITIES,
    ticketTypeOptions: ZENDESK_TICKET_TYPES,
    doneStatusOptions: ZENDESK_DONE_STATUSES,
    selectedStatuses,
    selectedPriorities,
    selectedTicketTypes,
    assignedToMe: Boolean(configuration.assignedToMe),
    tags: configuration.tags ?? "",
    selectedDoneStatus,
    views,
    isLoadingViews,
    hasError,
    selectedViewId: selectedView ? String(selectedView.id) : NONE_OPTION,
    selectedViewTitle: selectedView?.title ?? "",
    isStatusesMissing: isFilterMode && selectedStatuses.length === 0,
    isViewMissing: isViewMode && !selectedView,
    isDoneStatusMissing: !selectedDoneStatus,
    noneOptionValue: NONE_OPTION,

    onSelectMode: (nextMode: ZendeskMode) => {
      update((cfg) => ({ ...cfg, mode: nextMode }));
    },

    onSelectStatuses: (statuses: string[]) => {
      update((cfg) => ({ ...cfg, statuses: pickKnown(ZENDESK_STATUSES, statuses) }));
    },

    onSelectPriorities: (priorities: string[]) => {
      update((cfg) => ({ ...cfg, priorities: pickKnown(ZENDESK_PRIORITIES, priorities) }));
    },

    onSelectTicketTypes: (ticketTypes: string[]) => {
      update((cfg) => ({
        ...cfg,
        ticketTypes: pickKnown(ZENDESK_TICKET_TYPES, ticketTypes),
      }));
    },

    onToggleAssignedToMe: (value: boolean) => {
      update((cfg) => ({ ...cfg, assignedToMe: value }));
    },

    onChangeTags: (value: string) => {
      update((cfg) => ({ ...cfg, tags: value.trim() ? value : null }));
    },

    onSelectDoneStatus: (status?: string) => {
      const doneStatus = ZENDESK_DONE_STATUSES.find((option) => option === status) ?? null;
      update((cfg) => ({ ...cfg, doneStatus }));
    },

    onSelectView: (viewId?: string) => {
      const view = views.find((option) => String(option.id) === viewId) ?? null;
      update((cfg) => ({ ...cfg, view }));
    },
  };
};
